/**
 * Policy Baseline Cache Service
 * 
 * This service implements a two-tier caching system for policy projections:
 * 1. Baseline projections cache (location-specific, expensive to calculate)
 * 2. Policy transformations (instant, lightweight math operations)
 * 
 * The baseline includes user customizations for VMT, electricity, and natural gas.
 * When any of these change, the baseline is recalculated.
 * When policy levers change, only lightweight transformations are applied.
 */

import { TimeSeriesEmissionData } from './policyTimeSeriesService';
import { getVMTDataForGHG, VMTDataPoint } from './vmtDataService';
import { getBuildingsEnergyData, BuildingsEnergyData } from './buildingsEnergyDataService';

/**
 * Hash function to create a stable hash of a string
 */
function hashString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Hash function for VMT data
 */
export function hashVMTData(data: VMTDataPoint[]): string {
  const str = JSON.stringify(data.map(d => ({ year: d.year, vmt: d.vmt })));
  return hashString(str);
}

/**
 * Hash function for BuildingsEnergyData
 */
export function hashBuildingsEnergyData(data: BuildingsEnergyData): string {
  const elecRes = JSON.stringify(data.electricity.residential.values);
  const elecCom = JSON.stringify(data.electricity.commercial.values);
  const gasRes = JSON.stringify(data.naturalGas.residential.values);
  const gasCom = JSON.stringify(data.naturalGas.commercial.values);
  const combined = elecRes + elecCom + gasRes + gasCom;
  return hashString(combined);
}

export interface BaselineProjections {
  location: string;
  vmtHash: string;
  energyHash: string;
  historicalData: TimeSeriesEmissionData[]; // 2005-2023
  baselineProjections: TimeSeriesEmissionData[]; // 2023-2045, no policies
  baselineVMT: number; // 2023 VMT value
  currentEVPercent: number;
  solarCapacityKw: number; // 2023 solar capacity (for grid intensity calculations)
  population: number; // 2023 population (for grid intensity calculations)
  emfacData: any[]; // Cached EMFAC data
  populationData: Record<number, number>; // Cached population by year
  buildingsEnergyData: BuildingsEnergyData; // Cached electricity and natural gas data
  electricityCarbonIntensityGPerKwhByYear?: Record<number, number>; // gCO2e/kWh (from Buildings page carbon intensity chart)
  calculationDate: Date;
}

// Global baseline cache (one per location)
const baselineCache = new Map<string, BaselineProjections>();

// Track ongoing baseline calculations to prevent concurrent calculations for the same location
const calculatingBaselines = new Map<string, Promise<BaselineProjections>>();

/**
 * Get cache key for baseline projections
 */
function getBaselineCacheKey(location: string): string {
  return `baseline:${location}`;
}

/**
 * Check if baseline cache is valid for the current data state
 */
export async function isBaselineCacheValid(location: string): Promise<boolean> {
  const cacheKey = getBaselineCacheKey(location);
  const cached = baselineCache.get(cacheKey);
  
  if (!cached) {
    return false;
  }
  
  // Check if VMT data has changed
  const vmtData = await getVMTDataForGHG(location);
  const currentVmtHash = hashVMTData(vmtData);
  if (cached.vmtHash !== currentVmtHash) {
    console.log('🔄 Baseline cache invalid: VMT data changed');
    return false;
  }
  
  // Check if energy data has changed
  const energyData = await getBuildingsEnergyData(location);
  const currentEnergyHash = hashBuildingsEnergyData(energyData);
  if (cached.energyHash !== currentEnergyHash) {
    console.log('🔄 Baseline cache invalid: Energy data changed');
    return false;
  }
  
  return true;
}

/**
 * Get cached baseline projections if valid
 */
export function getCachedBaseline(location: string): BaselineProjections | null {
  const cacheKey = getBaselineCacheKey(location);
  return baselineCache.get(cacheKey) || null;
}

/**
 * Store baseline projections in cache
 */
export function setCachedBaseline(location: string, baseline: BaselineProjections): void {
  const cacheKey = getBaselineCacheKey(location);
  baselineCache.set(cacheKey, baseline);
  console.log('✅ Baseline projections cached for', location);
}

/**
 * Clear baseline cache for a location (when data changes)
 */
export function clearBaselineCache(location?: string): void {
  if (location) {
    const cacheKey = getBaselineCacheKey(location);
    baselineCache.delete(cacheKey);
    clearCalculationPromise(location);
    console.log('🗑️ Baseline cache cleared for', location);
  } else {
    baselineCache.clear();
    calculatingBaselines.clear();
    console.log('🗑️ All baseline caches cleared');
  }
}

/**
 * Get all cached locations
 */
export function getCachedLocations(): string[] {
  return Array.from(baselineCache.keys()).map(key => key.replace('baseline:', ''));
}

/**
 * Get or create a promise for an ongoing baseline calculation
 * This prevents concurrent calculations for the same location
 */
export function getOrCreateCalculationPromise(
  location: string,
  calculateFn: () => Promise<BaselineProjections>
): Promise<BaselineProjections> {
  const cacheKey = getBaselineCacheKey(location);
  const existing = calculatingBaselines.get(cacheKey);
  
  if (existing) {
    console.log('⏳ Baseline calculation already in progress for', location, '- waiting for existing calculation...');
    return existing;
  }
  
  const promise = calculateFn().then(
    (result) => {
      // Remove from calculating map when done
      calculatingBaselines.delete(cacheKey);
      return result;
    },
    (error) => {
      // Remove from calculating map on error
      calculatingBaselines.delete(cacheKey);
      throw error;
    }
  );
  
  calculatingBaselines.set(cacheKey, promise);
  return promise;
}

/**
 * Clear a calculation promise (e.g., when cache is cleared)
 */
export function clearCalculationPromise(location?: string): void {
  if (location) {
    const cacheKey = getBaselineCacheKey(location);
    calculatingBaselines.delete(cacheKey);
  } else {
    calculatingBaselines.clear();
  }
}

